import { isNonReplicatedField } from "../../db/nonReplicatedColumns";
import { dcrClientPending } from "./dcr";
import {
  type ConnectorFieldSource,
  getTargetValue,
  isItemVisible,
} from "./getTargetValue";
import { connectorsByIdentifier } from "./index";
import { listRows } from "./listRows";
import { visitorGrantReady } from "./visitorGrantReady";
import type { ConnectorDefinition, StepItem } from "./types";

export type ConnectorLike = ConnectorFieldSource & {
  identifier: string;
  backingWorkflowId?: string | null;
  authorizationMode: string | null;
  secretKeys?: string[] | null;
};

function visibleItems(app: ConnectorLike, definition: ConnectorDefinition) {
  return definition.steps
    .flatMap((step) => step.items)
    .filter((i) => isItemVisible(i, app, definition));
}

function usesOAuth(items: StepItem[]) {
  return items.some((i) => i.kind === "oauth");
}

function awaitsOwnerConnection(app: ConnectorLike, items: StepItem[]) {
  return usesOAuth(items) && app.oauthConnection?.status === "not_connected";
}

export function isConfigured(app: ConnectorLike): boolean {
  const definition = connectorsByIdentifier[app.identifier];
  if (!definition) return true;
  const items = visibleItems(app, definition);
  const inputs = items.filter(
    (i) =>
      i.kind === "input" ||
      i.kind === "url" ||
      i.kind === "hostname" ||
      i.kind === "select"
  );
  const requiredInputs = inputs.filter((i) => i.required);

  if (
    requiredInputs.some(
      (i) => !isNonReplicatedField(i.target) && !getTargetValue(i.target, app)
    )
  ) {
    return false;
  }

  const requiredListEmpty = items.some(
    (i) =>
      i.kind === "list" &&
      i.required &&
      listRows(app.configuration, i.prefix).length === 0
  );
  if (requiredListEmpty) return false;

  const requiredFileMissing = items.some(
    (i) =>
      i.kind === "file" &&
      i.required &&
      (i.target.kind === "secrets"
        ? !app.secretKeys?.includes(i.target.field)
        : !getTargetValue(i.target, app))
  );
  if (requiredFileMissing) return false;

  if (
    items.some((item) => item.kind === "workflow-picker") &&
    !app.backingWorkflowId
  ) {
    return false;
  }

  if (app.authorizationMode === "visitor" && canAuthorizeVisitors(app)) {
    return !dcrClientPending(
      definition.oauth,
      app.configuration,
      app.oauthClientId
    );
  }

  return !awaitsOwnerConnection(app, items);
}

export function canAuthorizeVisitors(app: ConnectorLike): boolean {
  const definition = connectorsByIdentifier[app.identifier];
  return (
    !!definition &&
    !!app.oauthConnection &&
    definition.protocol === "http" &&
    definition.oauth?.grantType(app.configuration) === "authorization_code" &&
    (definition.oauth.clientCredentialsFrom !== "db" || !!app.oauthClientId) &&
    usesOAuth(visibleItems(app, definition))
  );
}

export function readyForConnectionTest(
  app: ConnectorLike,
  callerGrant?: { status: string; scopes: readonly string[] }
): boolean {
  if (app.fresh) return false;
  return app.authorizationMode === "visitor"
    ? visitorGrantReady(app.oauthRequestedScopes ?? [], callerGrant) &&
        isConfigured(app)
    : isConfiguredForConnectionTest(app);
}

export function isConfiguredForConnectionTest(app: ConnectorLike): boolean {
  return isConfigured({ ...app, authorizationMode: "owner" });
}

export function isDraft(app: ConnectorLike): boolean {
  if (app.fresh) return true;
  return !isConfigured(app);
}
